import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ViewEncapsulation,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { Permissions } from 'account-hybrid/core/authentication';
import { IdentityService } from 'account-hybrid/core/authentication/services/identity.service';
import {
  CurationFullNamePipe,
} from '../../../../features/stories/curation-card/curation-card.pipes';
import { CurationBackgroundImageDirective } from '../../../../features/stories/curation-card/background-image.directive';
import { FtRatingComponent } from './ft-rating.component';
import { FtBulkCurationNavComponent } from './ft-bulk-curation-nav.component';
import { TranslateModule } from '@ngx-translate/core';
import {
  CurationDateFormatPipe,
  CurationDateRangePipe,
  CurationIsNowInRangePipe,
  WinnerPlacePipe,
} from './curation-modal.pipes';

/**
 * Primary #curation panel: the two-column story/curation view. Presentational — state
 * arrives as @Input()s and every action is an @Output() the host wires back to the
 * curation handlers. Pure hasPhoto/hasCaption/... helpers ported from the v1 directive.
 * CUSTOM_ELEMENTS_SCHEMA tolerates the <ft-image> avatar wrapper.
 */
@Component({
    selector: 'ft-curation-modal-ng',
    imports: [
        CurationBackgroundImageDirective,
        CurationFullNamePipe,
        TranslateModule,
        WinnerPlacePipe,
        CurationDateRangePipe,
        CurationDateFormatPipe,
        CurationIsNowInRangePipe,
        FtRatingComponent,
        FtBulkCurationNavComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './ft-curation-modal.component.html'
})
export class FtCurationModalComponent {
    /** The curation guest/story (mutated in place by the AngularJS controller's events). */
    readonly guest = input<any>();
    /** v1 `isShowTimeline` — shows the "View timeline" button. */
    readonly isShowTimeline = input(false);
    /**
     * TERMINAL CURATION CUT — bulk-curation header state. When set (bulk mode), the
     * `.ft-curation-nav` renders at the top of `.modal-header`. `null` in non-bulk mode
     * (the v1 directive only injected the header when `attrs.bulk` was defined).
     */
    readonly bulkNav = input<{ hasPhoto: boolean; isDone: boolean; total: number } | null>(null);
    /** v1 bulk header `next()` → curationModal.moveNext() (the host owns the work). */
    readonly bulkNext = output<void>();

    // Actions — the v1 controller still owns the work; Step 2 wires these back to it.
    readonly ratePhoto = output<number>();
    readonly rateCaption = output<number>();
    readonly rateQuote = output<number>();
    readonly advanceToFinal = output<any>();
    readonly pass = output<any>();
    readonly showImageModal = output<void>();
    readonly showReplaceModal = output<void>();
    readonly reprocessImage = output<void>();
    readonly downloadHighResImage = output<void>();
    readonly openAdvocatePage = output<void>();
    readonly broadcastStory = output<void>();

    /** v1 `scope.isPiiInfoVisible` — purely local UI state (the v1 controller's only use of
     * it is `showPiiInfo` flipping it true; no other consumer), so it lives in the component. */
    protected readonly isPiiInfoVisible = signal(false);

    private readonly parse: any = inject(ParseService);

    /**
     * `identity` is the Angular `IdentityService` (providedIn root) — the SAME service the
     * baseline's share-pane `ft-permissions` gate (`PermissionsComponent`) uses, so
     * `hasPermission`/`isFliptoStaff`/`hasPiiPermissions` match the frozen baseline exactly.
     */
    protected readonly identity = inject(IdentityService);

    /** v1 `scope.showPiiInfo` → reveal the PII rows. */
    protected showPiiInfo(): void {
        this.isPiiInfoVisible.set(true);
    }

    /** v1 share-pane `ft-permissions="StoriesWrite"` (PermissionsComponent → IdentityService). */
    protected get canWriteStories(): boolean {
        return this.identity.hasPermission(Permissions.StoriesWrite);
    }

    // --- pure helpers, verbatim from curationModal.directive.js ---

    protected hasPhoto(): boolean {
        const guest = this.guest();
        return guest && guest.poststay && guest.poststay.photo !== undefined;
    }

    protected hasCaption(): boolean {
        const guest = this.guest();
        return (
            guest &&
            guest.poststay &&
            guest.poststay.photo &&
            guest.poststay.photo.caption &&
            guest.poststay.photo.caption.length > 0
        );
    }

    protected hasPositiveQuote(): boolean {
        const guest = this.guest();
        return (
            guest &&
            guest.poststay &&
            guest.poststay.quote &&
            guest.poststay.quote.notesPositive &&
            this.parse.bool(guest.poststay.quote.isPositive)
        );
    }

    protected hasQuote(): boolean {
        const guest = this.guest();
        return (
            guest && guest.poststay && guest.poststay.quote && guest.poststay.quote.notesPositive !== undefined
        );
    }

    protected isInvitee(): boolean {
        const guest = this.guest();
        return guest && this.parse.bool(guest.isInvitee);
    }

    /**
     * FTK-6860 — a privacy-deleted guest keeps no actionable surface: the PII reveal, the
     * timeline link and both share panes stay hidden. Ported from the v1 curationModal.html
     * `&& !guest.piiDeleteDate` guards, which came off with the AngularJS tree in 1.76.1.
     */
    protected isPiiDeleted(): boolean {
        return !!this.guest()?.piiDeleteDate;
    }

    protected isNotReviewed(photo: any): boolean {
        return photo.photoContest.isClosed && !(photo.isPassed || photo.isFinalist);
    }

    protected hasAiTags(): boolean {
        const guest = this.guest();
        return (
            guest &&
            guest.poststay &&
            guest.poststay.photo &&
            guest.poststay.photo.ai &&
            guest.poststay.photo.ai.tags &&
            guest.poststay.photo.ai.tags.length > 0
        );
    }

    /** v1 inline `ng-click="guest.poststay.photo.isPassed = false"`. */
    protected setPassedFalse(): void {
        this.guest().poststay.photo.isPassed = false;
    }
}
